'use client';

import DateRangePicker from '@umodeapporg/ui/components/DateTime/DateRangePicker';
import ControlledViewToggle, {
  IControlledViewToggleOption,
} from '@umodeapporg/ui/components/Form/ControlledViewToggle';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IPeriodoDaVisao, PERIODO_PADRAO, TPeriodoAtalho } from '../helpers/periodoDaVisao';

/** Os atalhos na ordem em que eles alargam a janela. */
const ATALHOS: ReadonlyArray<TPeriodoAtalho> = ['QUATRO', 'DOZE', 'TUDO'];

/** The toggle's own value for the typed range, which has no shortcut of its own. */
const PERSONALIZADO = 'PERSONALIZADO';

type TOpcaoDoPeriodo = TPeriodoAtalho | typeof PERSONALIZADO;

/** An ISO day as the picker reads it: midnight UTC, the timezone it formats back in. */
function diaDoIso(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

interface PeriodoSeletorProps {
  periodo: IPeriodoDaVisao;
  /**
   * The window on screen right now, in ISO. "De / até" opens on it: an empty range cuts
   * nothing, and the link would read it back as the default.
   */
  intervaloVisivel: { de: string; ate: string };
  onChange: (periodo: IPeriodoDaVisao) => void;
}

/**
 * O período que a capacidade e a pizza compartilham. Fica ACIMA das duas, e não dentro de
 * um dos blocos, porque ele manda nos dois: dentro de um, pareceria filtro só daquele.
 */
export function PeriodoSeletor({ periodo, intervaloVisivel, onChange }: PeriodoSeletorProps) {
  const { t } = useTranslation('common');

  const personalizado = periodo.atalho === null;

  const opcoes = useMemo<Array<IControlledViewToggleOption<TOpcaoDoPeriodo>>>(
    () => [
      ...ATALHOS.map((atalho) => ({ value: atalho, label: t(`visaoExecutiva.periodo.${atalho}`) })),
      { value: PERSONALIZADO, label: t('visaoExecutiva.periodo.personalizado') },
    ],
    [t],
  );

  // Kept stable: the picker resets its own draft whenever this object changes.
  const intervaloDoPicker = useMemo(
    () =>
      periodo.de !== null && periodo.ate !== null
        ? { from: diaDoIso(periodo.de), to: diaDoIso(periodo.ate) }
        : undefined,
    [periodo.ate, periodo.de],
  );

  const handleOpcao = (opcao: TOpcaoDoPeriodo): void => {
    if (opcao === PERSONALIZADO) {
      if (!personalizado) {
        onChange({ atalho: null, de: intervaloVisivel.de, ate: intervaloVisivel.ate });
      }
      return;
    }
    onChange({ atalho: opcao, de: null, ate: null });
  };

  const handleIntervalo = ({ from, to }: { from: string | undefined; to: string | undefined }): void => {
    // Cleared, the range cuts nothing: back to the default, as an empty link reads.
    if (from === undefined && to === undefined) {
      onChange(PERIODO_PADRAO);
      return;
    }
    // Only a closed range reaches the link; the first click is still half a choice.
    if (from !== undefined && to !== undefined) {
      onChange({ atalho: null, de: from, ate: to });
    }
  };

  return (
    // Tudo numa LINHA só, ao lado do título da fileira: numa linha própria o seletor
    // custava uma faixa inteira de altura, e a tela é um relatório de uma página.
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-foreground-muted">{t('visaoExecutiva.periodo.rotulo')}</span>
        <ControlledViewToggle<TOpcaoDoPeriodo>
          options={opcoes}
          value={periodo.atalho ?? PERSONALIZADO}
          onChange={handleOpcao}
          size="sm"
          showLabels
          syncWithUrl={false}
        />
      </div>

      {personalizado && (
        <div className="w-64">
          <DateRangePicker
            label={t('visaoExecutiva.periodo.personalizado')}
            startValue={intervaloDoPicker}
            onUpdate={handleIntervalo}
            useIcon
          />
        </div>
      )}
    </div>
  );
}
